import React, { useState } from "react";
import {
	DatePicker,
	Button,
	Row,
	Col,
	Space,
	Spin,
	Descriptions,
	Result,
	Modal,
	Grid,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import Text from "components/input-types/Text";
import TextArea from "components/input-types/TextArea";
import Select from "components/input-types/Select";
import {
	deleteLeaveLines,
	getNewLeaveLines,
	leaveLineAdded,
	leaveLineDeleted,
	leaveLineEdited,
	selectAllLeaveLines,
	selectLeaveError,
	selectLeaveLinesStatus,
} from "./leaveLineSlice";
import { useDispatch, useSelector } from "react-redux";
import {
	cpeFormEdited,
	selectCpeFormLeaveComments,
} from "../cpeForm/cpeFormSlice";
import dayjs from "dayjs";
import moment from "moment";
import { makeSelectLookupByDomainCode } from "reducers";
import {
	QuestionInputCol,
	QuestionLabelCol,
	Section,
} from "components/QnaComponents";
import { postLeaveLines } from "./leaveLineSlice";
import InputModal from "components/InputModal";
import {
	selectCmeFormId,
	selectFormStatus,
	selectIsCmeLocked,
} from "reducers/formSlice";
import HelpTooltip from "../Components/HelpTooltip";
import {
	requestFormEdited,
	selectRequestFormLeaveComments,
} from "../requestForm/requestFormSlice";
import { selectParentFormData } from "../parentForm/parentFormSlice";

const dateFormat = "DD/MM/YYYY";

const Leave = ({ type }) => {
	const [dateRange, setDateRange] = useState({});
	const [isModalVisible, setIsModalVisible] = React.useState(false);
	const status = useSelector(selectLeaveLinesStatus);
	const formId = useSelector(selectCmeFormId);
	const leaveLines = useSelector(selectAllLeaveLines);
	const dispatch = useDispatch();
	const isDateRangeFilledOut = dateRange.from && dateRange.to;
	const onDateRangeChange = async (dates) => {
		if (isDateRangeFilledOut) {
			const data = getNewLeaveLines({
				formId,
				from: dateRange.from.toDate(),
				to: dateRange.to.subtract(1, "days").toDate(),
				type: type,
			});
			dispatch(postLeaveLines(data));
		}
	};
	const formStatus = useSelector(selectFormStatus);
	const isLocked = useSelector(selectIsCmeLocked);
	const disabled = isLocked;
	const errors = useSelector(selectLeaveError);

	//when CME leave claim, we need to override the leave summary type
	const { formTypeId: parentFormTypeId } = useSelector(selectParentFormData);
	const isCmeLeaveClaimForm = parentFormTypeId === 1 && type === "leaveClaim";

	//#region insert date modal
	const maxDate =
		leaveLines.length >= 1
			? moment(
					leaveLines.length > 1
						? leaveLines.reduce(function (a, b) {
								return a.leaveDate > b.leaveDate
									? a.leaveDate
									: b.leaveDate;
						  })
						: leaveLines[0].leaveDate,
					"YYYY-MM-DD"
			  ).add(1, "day")
			: null;

	const [newLeaveDate, setNewLeaveDate] = React.useState(maxDate);
	const onDateInsert = () => {
		setNewLeaveDate(maxDate);
		setIsModalVisible(true);
	};
	const onReset = () => {
		dispatch(deleteLeaveLines({ formId }));
	};
	const onDatePicked = (date) => {
		setNewLeaveDate(date);
	};
	const onOk = () => {
		const data = getNewLeaveLines({
			formId,
			from: newLeaveDate.toDate(),
			to: newLeaveDate.toDate(),
			type: type,
		});
		dispatch(postLeaveLines(data));
		setIsModalVisible(false);
	};
	const modalFooter = [
		<Button key="cancel" onClick={() => setIsModalVisible(false)}>
			Cancel
		</Button>,
		<Button key="ok" type="primary" onClick={onOk}>
			OK
		</Button>,
	];
	const modalContent = (
		<>
			<Space direction="vertical">
				<Row>
					<QuestionLabelCol labelColWidth={24} title="Leave Date" />
					<QuestionInputCol inputColWidth={24}>
						<DatePicker
							allowClear={false}
							format={dateFormat}
							onChange={onDatePicked}
							value={newLeaveDate}
						/>
					</QuestionInputCol>
				</Row>
				<p>
					Specify the date of leave and click OK. You can fill out the
					remaining details on the main form.
				</p>
				<p></p>
			</Space>
		</>
	);
	//#endregion

	//for editing leave comments
	const leaveCommentsCpe = useSelector(selectCpeFormLeaveComments);
	const leaveCommentsReq = useSelector(selectRequestFormLeaveComments);
	const onLeaveCommentEdited = (action) => {
		if (type === "cpeLeaveRequest") dispatch(cpeFormEdited(action));
		else dispatch(requestFormEdited(action));
	};

	const { useBreakpoint } = Grid;

	const screens = useBreakpoint();

	return status === "failed" || formStatus === "failed" ? (
		<Result
			status="error"
			title="An error has occurred"
			subTitle="Please close this form and try a new form, and if issues persist, contact support."
		/>
	) : leaveLines.length === 0 && status !== "loading" ? (
		<Section title="Select Leave Period">
			<QuestionInputCol inputColWidth={24}>
				<Row gutter={[10, 10]} align="middle">
					<Col span={4} md={2}>
						From
					</Col>
					<Col span={20} md={8}>
						<DatePicker
							placeholder="First day of leave"
							format={dateFormat}
							disabled={disabled}
							onChange={(value) =>
								setDateRange({ ...dateRange, from: value })
							}
							style={{ width: 150 }}
						/>
					</Col>
					<Col span={4} md={2}>
						To
					</Col>
					<Col span={20} md={8}>
						<DatePicker
							placeholder="Return to work date"
							format={dateFormat}
							disabled={disabled}
							onChange={(value) =>
								setDateRange({ ...dateRange, to: value })
							}
							style={{ width: 150 }}
						/>
					</Col>
					<Col span={24}>
						<Button
							type="primary"
							disabled={!isDateRangeFilledOut}
							onClick={() => onDateRangeChange()}
						>
							OK
						</Button>
					</Col>
				</Row>
			</QuestionInputCol>
			{errors &&
				errors.map((e, key) => (
					<span
						key={key}
						className="qna-question-form-item-validation"
					>
						{e}
					</span>
				))}
		</Section>
	) : status === "loading" ? (
		<Spin tip="Loading..." />
	) : leaveLines.length > 0 ? (
		<>
			<InputModal
				title="Add Additional Leave Request"
				visible={isModalVisible}
				footer={modalFooter}
			>
				{modalContent}
			</InputModal>
			<Section
				title={
					type === "cpeLeaveRequest"
						? "CME Leave Details"
						: "Leave Details"
				}
			>
				<QuestionLabelCol labelColWidth={24}>
					{type === "cpeLeaveRequest" ? (
						"Please complete this section to provide daily details of planned leave then click Next"
					) : type === "smoLeaveRequest" ? (
						<>
							Please complete this section to provide daily
							details of planned leave then click Next. You can
							check your current balances in Kiosk.&nbsp;
							<a
								href="http://staffnet/KioskApplication"
								rel="noreferrer"
								target="_blank"
							>
								Click here to open a new window to Kiosk (onsite
								access only)
							</a>
						</>
					) : (
						<></>
					)}
				</QuestionLabelCol>
				<LeaveContainer
					type={type}
					leaveLines={leaveLines}
					disabled={disabled}
					screens={screens}
				/>
				{errors &&
					errors.map((e, key) => (
						<span
							key={key}
							className="qna-question-form-item-validation"
						>
							{e}
						</span>
					))}
				<QuestionInputCol inputColWidth={24}>
					<Space>
						{!disabled ? (
							<>
								<Button onClick={onDateInsert} type="primary">
									+ Insert Another Day
								</Button>
								<Button onClick={onReset} type="primary">
									Reset Form
								</Button>
							</>
						) : (
							<></>
						)}
					</Space>
				</QuestionInputCol>
			</Section>
			<Section title="Leave Summary">
				<Row>
					<Col span={24} md={16} style={{ paddingRight: 20 }}>
						<LeaveSummaryContainer
							type={
								isCmeLeaveClaimForm ? "cpeLeaveRequest" : type
							}
							leaveLines={leaveLines}
						/>
					</Col>
					<Col span={24} md={8}>
						<QuestionLabelCol labelColWidth={24} title="Comments" />
						<QuestionInputCol inputColWidth={24}>
							<TextArea
								dispatch={[
									onLeaveCommentEdited,
									onLeaveCommentEdited,
								]}
								name="leaveComments"
								defaultValue={
									type === "cpeLeaveRequest"
										? leaveCommentsCpe
										: leaveCommentsReq
								}
								rows={5}
								disabled={disabled}
							/>
						</QuestionInputCol>
					</Col>
				</Row>
			</Section>
		</>
	) : (
		<>An error has occurred!</>
	);
};

const LeaveContainer = ({ leaveLines, type, disabled, screens }) => {
	const selectCategoryLookup = makeSelectLookupByDomainCode("leave");
	const categoryLookup = useSelector(selectCategoryLookup);

	//type of leave form determines which lookup lists to use
	const filteredCategoryLookup =
		type === "cpeLeaveRequest"
			? categoryLookup.filter(
					(l) =>
						l.description === "CME" ||
						l.description === "CME Travel" ||
						l.description === "Rostered Day Off"
			  )
			: type === "leaveClaim"
			? categoryLookup
			: categoryLookup.filter(
					(l) =>
						l.description !== "CME" &&
						l.description !== "CME Travel"
			  );

	const dispatch = useDispatch();

	const dispatchProp = [
		(action) => dispatch(leaveLineAdded(action)),
		(action) => dispatch(leaveLineEdited(action)),
	];

	const unit = leaveLines.length > 0 ? leaveLines[0].unitType : "";

	return (
		<div style={{ paddingBottom: 20 }}>
			{screens?.md ? <LeaveDateRowHeading unit={unit} /> : null}
			{leaveLines.map((leave, key) => (
				<LeaveDateRow
					key={key}
					data={leave}
					dispatch={dispatchProp}
					categoryLookup={[
						{
							id: "",
							code: null,
							description: "",
							domainCode: "leave",
						},
						...filteredCategoryLookup,
					]}
					disabled={disabled}
					screens={screens}
					unit={unit}
				/>
			))}
		</div>
	);
};

const LeaveDateRowHeading = ({ unit }) => {
	return (
		<Row>
			<QuestionLabelCol span={0} md={4}>
				Date
			</QuestionLabelCol>
			<QuestionLabelCol span={8} md={0}>
				Date
			</QuestionLabelCol>
			<QuestionLabelCol span={4} md={2} title={unit} isMandatory />
			<QuestionLabelCol span={12} md={9}>
				Leave Type{" "}
				<HelpTooltip>
					For help on leave submissions refer to the CME Guidelines
				</HelpTooltip>
			</QuestionLabelCol>
			<QuestionLabelCol span={0} md={7}>
				Details{" "}
				<HelpTooltip>
					Please provide any details to assist the CME coordinator,
					for example any cover arrangements
				</HelpTooltip>
			</QuestionLabelCol>
		</Row>
	);
};

const LeaveDateRow = ({
	data,
	dispatch,
	categoryLookup,
	disabled,
	screens,
	unit,
}) => {
	const { leaveId, leaveDate, categoryLookupId, description, numberOfUnits } =
		data;
	const isNew = !leaveId;
	const commonProps = { isNew, dispatch, name: leaveId };
	const date = dayjs(leaveDate, "YYYY-MM-DD");
	//for deleting a single line
	const [visible, setVisible] = useState(false);
	const disp = useDispatch();
	const deleteLeave = async () => {
		await disp(postLeaveLines([{ ...data, isDeleted: true }])).unwrap();
		disp(leaveLineDeleted(data.leaveId));
	};

	//add modal for confirmation here

	return (
		<>
			<LeaveLineDeleteModal
				visible={visible}
				onOk={() => {
					deleteLeave();
					setVisible(false);
				}}
				onCancel={() => setVisible(false)}
			/>
			{!screens?.md ? <LeaveDateRowHeading unit={unit} /> : null}
			<Row align="middle">
				<QuestionInputCol span={8} md={4}>
					<LeaveDateDisplay date={date} />
				</QuestionInputCol>
				<QuestionInputCol span={4} md={2}>
					<Text
						questionId="numberOfUnits"
						{...commonProps}
						defaultValue={numberOfUnits}
						dataType="numeric"
						disabled={disabled}
					/>
				</QuestionInputCol>
				<QuestionInputCol span={12} md={9}>
					<Select
						questionId="categoryLookupId"
						{...commonProps}
						defaultValue={categoryLookupId}
						lookup={categoryLookup}
						disabled={disabled}
						placeholder="Pick leave type"
						options={{ valueField: "id", showCode: true }}
					/>
				</QuestionInputCol>
				{screens?.md ? (
					<>
						<QuestionInputCol span={22} md={7}>
							<Text
								questionId="description"
								{...commonProps}
								defaultValue={description}
								disabled={disabled}
								placeholder="Leave details"
							/>
						</QuestionInputCol>
						<QuestionInputCol inputColWidth={2}>
							<Button
								type="link"
								danger
								disabled={disabled}
								onClick={() => setVisible(true)}
							>
								<DeleteOutlined />
							</Button>
						</QuestionInputCol>
					</>
				) : null}
			</Row>
			{!screens?.md ? (
				<>
					<Row>
						<QuestionLabelCol span={24}>
							Details{" "}
							<HelpTooltip>
								Please provide any details to assist the CME
								coordinator, for example any cover arrangements
							</HelpTooltip>
						</QuestionLabelCol>
					</Row>
					<Row
						style={{
							paddingBottom: 10,
							borderBottom: "1px solid #ddd",
						}}
					>
						<QuestionInputCol span={22}>
							<Text
								questionId="description"
								{...commonProps}
								defaultValue={description}
								disabled={disabled}
							/>
						</QuestionInputCol>
						<QuestionInputCol inputColWidth={2}>
							<Button
								type="link"
								danger
								disabled={disabled}
								onClick={() => setVisible(true)}
							>
								<DeleteOutlined />
							</Button>
						</QuestionInputCol>
					</Row>
				</>
			) : null}
		</>
	);
};

const LeaveDateDisplay = ({ date }) => {
	return (
		<Col span={24}>
			{date.format("ddd")} {date.format("DD/MM/YY")}
		</Col>
	);
};

const LeaveSummaryContainer = ({ type, leaveLines }) => {
	//constants that are the pks from the database lookup table
	const CME = 246;
	const CME_TRAVEL = 247;
	const ANNUAL_LEAVE = 8;

	//reducer on array of obj need an initial value
	//reduce leave array to a sum of days
	const initialValue = 0;
	const cme = leaveLines.reduce((acc, val) => {
		return val.categoryLookupId === CME ? val.numberOfUnits + acc : acc;
	}, initialValue);
	const cmeTravel = leaveLines.reduce((acc, val) => {
		return val.categoryLookupId === CME_TRAVEL
			? val.numberOfUnits + acc
			: acc;
	}, initialValue);
	const other = leaveLines.reduce((acc, val) => {
		return val.categoryLookupId !== CME_TRAVEL &&
			val.categoryLookupId !== CME
			? val.numberOfUnits + acc
			: acc;
	}, initialValue);

	const al = leaveLines.reduce((acc, val) => {
		return val.categoryLookupId === ANNUAL_LEAVE
			? val.numberOfUnits + acc
			: acc;
	}, initialValue);
	const nonAl = leaveLines.reduce((acc, val) => {
		return val.categoryLookupId !== ANNUAL_LEAVE
			? val.numberOfUnits + acc
			: acc;
	}, initialValue);

	return type === "cpeLeaveRequest" ? (
		<Descriptions title="" bordered size="small" column={3}>
			<Descriptions.Item
				span={3}
				label="CME Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{cme}
			</Descriptions.Item>
			<Descriptions.Item
				span={3}
				label="CME Travel Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{cmeTravel}
			</Descriptions.Item>
			<Descriptions.Item
				span={3}
				label="Other Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{other}
			</Descriptions.Item>
			<Descriptions.Item
				span={3}
				label="Total Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{cme + cmeTravel + other}
			</Descriptions.Item>
		</Descriptions>
	) : (
		<Descriptions title="" bordered size="small" column={3}>
			<Descriptions.Item
				span={3}
				label="Annual Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{al}
			</Descriptions.Item>
			<Descriptions.Item
				span={3}
				label="Other Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{nonAl}
			</Descriptions.Item>
			<Descriptions.Item
				span={3}
				label="Total Leave"
				labelStyle={{ textAlign: "left" }}
				contentStyle={{ textAlign: "right" }}
			>
				{al + nonAl}
			</Descriptions.Item>
		</Descriptions>
	);
};

const LeaveLineDeleteModal = ({ visible, onOk, onCancel }) => {
	return (
		<Modal
			title="Delete Confirmation"
			open={visible}
			onOk={onOk}
			onCancel={onCancel}
			okText="Delete"
		>
			<p>Are you sure you want to delete this line?</p>
		</Modal>
	);
};

export default Leave;
